import { useMemo } from 'react';
import { useUser } from '../../userManagement/hooks';

import { useQuery } from '@tanstack/react-query';
import { get } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { NewCountResponse } from '../types';

export const REGISTRATION_REQUESTS_READ =
  'app:dxm-user-registration-requests:permissions:read';
export const REGISTRATION_REQUESTS_WRITE =
  'app:dxm-user-registration-requests:permissions:write';
export const SUPPORT_REQUESTS_READ =
  'app:dxm-user-support-requests:permissions:read';
export const SUPPORT_REQUESTS_WRITE =
  'app:dxm-user-support-requests:permissions:write';
export const INTAKE_REQUESTS_READ =
  'app:dxm-user-intake-requests:permissions:read';
export const INTAKE_REQUESTS_WRITE =
  'app:dxm-user-intake-requests:permissions:write';
export const WHITELIST_READ = 'app:dxm-manage-whitelist:permissions:read';
export const WHITELIST_WRITE = 'app:dxm-manage-whitelist:permissions:write';

export const useUserRequestsPermissions = () => {
  const { data: user, isLoading } = useUser('me', {
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  const userPermissions = user?.attributes;

  const registrationReadPermission = userPermissions?.includes(
    REGISTRATION_REQUESTS_READ,
  );
  const registrationWritePermission = userPermissions?.includes(
    REGISTRATION_REQUESTS_WRITE,
  );

  const supportReadPermission = userPermissions?.includes(
    SUPPORT_REQUESTS_READ,
  );
  const supportWritePermission = userPermissions?.includes(
    SUPPORT_REQUESTS_WRITE,
  );

  const intakeReadPermission = userPermissions?.includes(INTAKE_REQUESTS_READ);
  const intakeWritePermission = userPermissions?.includes(
    INTAKE_REQUESTS_WRITE,
  );

  const whitelistReadPermission = userPermissions?.includes(WHITELIST_READ);
  const whitelistWritePermission = userPermissions?.includes(WHITELIST_WRITE);

  const permissions = useMemo(() => {
    return {
      registration: {
        read: registrationReadPermission,
        write: registrationWritePermission,
      },
      support: {
        read: supportReadPermission,
        write: supportWritePermission,
      },
      intake: {
        read: intakeReadPermission,
        write: intakeWritePermission,
      },
      whitelist: {
        read: whitelistReadPermission,
        write: whitelistWritePermission,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return { permissions, isLoading };
};

export { useUserDateFormatter } from '../../../shared/hooks/useUserDateFormatter';

export const useHasAnyUserRequestPermission = () => {
  const { permissions, isLoading } = useUserRequestsPermissions();
  const hasPermission =
    !!permissions.registration.read ||
    !!permissions.registration.write ||
    !!permissions.support.read ||
    !!permissions.support.write ||
    !!permissions.intake.read ||
    !!permissions.intake.write;
  return { hasPermission, isLoading };
};

export const useRequestNewCount = () => {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['request-new-count'],
    queryFn: async () => {
      const idToken = await getIdToken();
      const response = await get<NewCountResponse>(
        `/requests/counters`,
        apiUrl,
        idToken,
      );
      return response.counters;
    },
    enabled: isSignedIn,
    retry: false,
    gcTime: 300000,
  });
};
